import { Delivery } from '@printdeal/commercetools-v2/types';
import { PriceAttribute } from '@printdeal/product-selector';
import {
  getCategoryName,
  isLineItemCancelled,
  getAttributeDescription,
  hasPickupPoint,
  isReviewGroupLead,
  hasReviewGroup,
  getAssociatedGroupedLineItems,
} from '@printdeal/business-logic';
import { mapAttributesToMcpAttributes } from '@printdeal/transformers';

// Helpers
import { getShippingAddressesFromLineItem } from '@printdeal/checkout-frontend';
import { Environment } from '../../../helpers/env';
import { splitAttributeAndValues } from '../../../helpers/LineItems';
// @ts-expect-error
import { getJsonFromUrl } from '../../../helpers/URLHelper';
import { getLanguageFromLocale } from '../../../helpers/translations';

// Types
import {
  OrderWithTotalDetails,
  SanitizedLineItem,
  SanitizedOrder,
} from '../../../types/order/Order';
import LineItem from '../../../types/cart/LineItem';
import { filterPriceAttributesFromSelection } from '../../product/selector/helpers/ProductRouteHelper';
import { pricePivotAttributes } from '../../../helpers/PricesHelper';
import { ISelection } from '../../../types/selector/Selector';

interface GroupSelectionForPrice {
  quantity: number,
  attributes: PriceAttribute[];
}

type IdenticalAttributes = Record<string, boolean>;
type FirstAttributeValues = Record<string, string | number>;

export interface Attribute {
  attributeName: string;
  attributeValue: string | number;
}

interface SelectionForPricing {
  variableAttributesForPrice: PriceAttribute[];
  groupedAttributesForPrice: GroupSelectionForPrice[];
  pivotAttributesForPrice: {
    pivotAttribute: string;
    order: string[];
  }[];
}

export interface SiblingAttributeData {
  quantity: number;
  productDescription: Attribute[];
  lineItemNumber: string | undefined;
}

const getSelectionForPricing = (lineItems: SanitizedLineItem[]): SelectionForPricing => {
  let pivotAttributeData: ISelection = {};

  // Convert the attribute data of the lineItems to a { name: string, value: string } construction.
  const userSelectionMap = lineItems
    .map((lineItem) => {
      const mcpAttributes: ISelection = mapAttributesToMcpAttributes(lineItem.custom.fields.userSelection);
      const noPricingAttributes = filterPriceAttributesFromSelection(mcpAttributes)
        .filter((attribute) => attribute.name !== 'groupedQuantity');

      // Extract the pivot attributes separately.
      pivotAttributeData = Object.keys(mcpAttributes).reduce<ISelection>((acc, attributeName) => {
        if (pricePivotAttributes.includes(attributeName)) {
          acc[attributeName as keyof ISelection] = mcpAttributes[attributeName];
        }
        return acc;
      }, {});

      return {
        quantity: lineItem.quantity,
        userSelection: noPricingAttributes,
      };
    });

  let variableAttributesForPrice: PriceAttribute[] = [];
  let groupedAttributesForPrice: GroupSelectionForPrice[] = [];

  if (userSelectionMap.length === 1) {
    // There is only one lineItem to begin with. Just return it.
    // eslint-disable-next-line prefer-destructuring
    variableAttributesForPrice = userSelectionMap[0].userSelection;
  } else {
    // We're dealing with a lineItemGroup, so we need to split the main attributes and the grouped attributes.
    const isIdentical: IdenticalAttributes = {};
    const firstValues: FirstAttributeValues = {};

    // Determine which attributes are which
    userSelectionMap.forEach((lineItem) => {
      lineItem.userSelection.forEach((attribute) => {
        if (!firstValues[attribute.name]) {
          firstValues[attribute.name] = attribute.value;
          isIdentical[attribute.name] = true;
        } else if (firstValues[attribute.name] !== attribute.value) {
          isIdentical[attribute.name] = false;
        }
      });
    });

    // Get the attribute data identical for all siblings.
    variableAttributesForPrice = userSelectionMap[0].userSelection
      .filter((attribute: PriceAttribute) => isIdentical[attribute.name]);

    // Get the attribute data which differs among the siblings, plus quantity.
    groupedAttributesForPrice = userSelectionMap
      .map((selectionData): GroupSelectionForPrice => ({
        quantity: selectionData.quantity,
        attributes: selectionData.userSelection
          .filter((attribute: PriceAttribute) => !isIdentical[attribute.name]),
      }));
  }

  const pivotAttributesForPrice = Object.keys(pivotAttributeData).map((attributeName) => ({
    pivotAttribute: attributeName,
    order: [pivotAttributeData[attributeName]],
  }));

  return {
    variableAttributesForPrice,
    groupedAttributesForPrice,
    pivotAttributesForPrice,
  };
};
const getProductDescriptionsForGroup = (lineItems: SanitizedLineItem[]): PriceAttribute[] => lineItems
  .map((lineItem) => lineItem.sane.productDescription
    .map((attribute: Attribute) => ({ name: attribute.attributeName, value: attribute.attributeValue }))).flat();

const getUniqueAndIdenticalAttributes = (productDescriptionsForGroup: PriceAttribute[]): IdenticalAttributes => {
  const isIdentical: IdenticalAttributes = {};
  const firstValues: FirstAttributeValues = {};

  productDescriptionsForGroup.forEach((attribute) => {
    if (!firstValues[attribute.name]) {
      firstValues[attribute.name] = attribute.value;
      isIdentical[attribute.name] = true;
    } else if (firstValues[attribute.name] !== attribute.value) {
      isIdentical[attribute.name] = false;
    }
  });

  return isIdentical;
};

const siblingAttributeData = (
  lineItems: SanitizedLineItem[],
  attributeStatus: IdenticalAttributes,
): SiblingAttributeData[] => lineItems
  .map((lineItem) => ({
    quantity: lineItem.sane.lineItemQuantity || lineItem.quantity,
    productDescription: lineItem.sane.productDescription
      .filter((attribute: Attribute) => !attributeStatus[attribute.attributeName]),
    lineItemNumber: lineItem.custom.fields.lineItemNumber,
  }));

const getProductName = (lineItem: LineItem, locale: string) => getCategoryName(lineItem, locale);

const getFinalDeliveryDate = ({
  revisedPromisedArrivalDateV2,
  revisedPromisedArrivalDate,
  promisedArrivalDate,
}: {
  revisedPromisedArrivalDateV2: string;
  revisedPromisedArrivalDate: string;
  promisedArrivalDate: string;
}) => revisedPromisedArrivalDateV2 || revisedPromisedArrivalDate || promisedArrivalDate;

/**
 * Extract deliveries for the given lineItemId from the full list of deliveries.
 * @param deliveries
 * @param lineItemId
 */
const getLineItemDeliveries = (
  deliveries: Delivery[],
  lineItemId: string,
) => deliveries.filter((delivery: Delivery) => (
  delivery.items.find((deliveryItem) => deliveryItem.id === lineItemId)
));

/**
 * Quick method to make sure both postal codes can be compared
 * Changes the code to lowercase and removes any whitespace from it
 * @param postalCode
 */
const formatPostalCode = (postalCode: string) => postalCode?.toLowerCase().replace(/\s+/g, '');

const splitDeliveriesByType = (deliveries: Delivery[], lineItemId: string) => {
  const lineItemDeliveries = getLineItemDeliveries(deliveries, lineItemId);

  // -- Split the deliveries by their postal code
  const postalCodeDeliveries: { [key: string]: string[] } = {};
  const unknownDeliveries: string[] = [];
  lineItemDeliveries.forEach(({ parcels }) => {
    parcels?.forEach(({ trackingData }) => {
      const url = trackingData?.providerTransaction;
      if (url) {
        const postalCode = formatPostalCode(getJsonFromUrl(url).P);
        if (postalCode) {
          if (!postalCodeDeliveries[postalCode]) {
            postalCodeDeliveries[postalCode] = [];
          }
          postalCodeDeliveries[postalCode].push(url);
        } else {
          unknownDeliveries.push(url);
        }
      }
    });
  });

  return { postalCodeDeliveries, unknownDeliveries };
};

const sanitizeLineItem = (
  lineItem: LineItem,
  deliveries: Delivery[],
  locale: string,
) => {
  const localeCode = getLanguageFromLocale(locale);

  // FETCH data from lineItem object for easy use
  const {
    custom, quantity, totalGroupQuantity, custom: { fields: { timedDelivery } },
  } = lineItem;

  // DECLARE new lineItem variables (which are not straightforward)
  const timedDeliveryInfo = timedDelivery && JSON.parse(timedDelivery);
  const shippingAddresses = getShippingAddressesFromLineItem(lineItem);
  const { postalCodeDeliveries, unknownDeliveries } = splitDeliveriesByType(deliveries, lineItem.id);
  const productDescription: Attribute[] = splitAttributeAndValues(getAttributeDescription(lineItem, localeCode) || '');

  // YIELD updated lineItem
  return {
    ...lineItem,
    sane: {
      productName: getProductName(lineItem, locale),
      lineItemQuantity: isReviewGroupLead(lineItem) && totalGroupQuantity ? totalGroupQuantity : quantity,
      finalDeliveryDate: getFinalDeliveryDate({
        revisedPromisedArrivalDateV2: custom.fields.revisedPromisedArrivalDateV2,
        revisedPromisedArrivalDate: custom.fields.revisedPromisedArrivalDate,
        promisedArrivalDate: custom.fields.promisedArrivalDate,
      }),
      timedDeliveryInfo,
      productDescription,
      siblingDescriptions: [],
      countProductDescription: productDescription.length,
      shippingAddresses,
      postalCodeDeliveries,
      unknownDeliveries,
      isCancelled: isLineItemCancelled(lineItem, Environment),
      hasPickupPoint: hasPickupPoint(lineItem),
    },
  };
};

// The sanitization is for the sake of the reOrder landing page, which is an OrderList and has limited data.
// Some additional sanitization is needed.
const sanitizeLineItemsForReOrder = (order: SanitizedOrder): SanitizedOrder => {
  const sanitizedLineItemsForReOrder = order.lineItems.map((lineItem) => {
    let selection: SelectionForPricing = {
      variableAttributesForPrice: [],
      groupedAttributesForPrice: [],
      pivotAttributesForPrice: [],
    };
    let siblingDescriptions: SiblingAttributeData[] = [];
    let { quantity } = lineItem;
    let { productDescription } = lineItem.sane;
    const originalTotalLineItemAmount = { ...lineItem.custom.fields.originalLineItemAmount };
    const discountedTotalProductPriceValue = { ...lineItem.custom.fields.discountedProductPriceValue };
    const originalTotalLineItemAmountWithTax = { ...lineItem.custom.fields.originalLineItemAmountWithTax };

    if (isReviewGroupLead(lineItem)) {
      const associatedLineItems = getAssociatedGroupedLineItems(
        order,
        lineItem,
        { reviewGroup: true },
      ) as SanitizedLineItem[];

      if (associatedLineItems.length > 0) {
        // Get the productDescriptions for the lineItems in the group.
        const productDescriptionsForGroup = getProductDescriptionsForGroup(
          [lineItem, ...associatedLineItems] as SanitizedLineItem[],
        );

        // Determine which attributes are unique, and which ones are not.
        const attributeStatus = getUniqueAndIdenticalAttributes(productDescriptionsForGroup);

        // The identical ones should be in the main list.
        productDescription = productDescription
          .filter((attribute: Attribute) => attributeStatus[attribute.attributeName]);
        // The differential ones should be in the sibling list.
        siblingDescriptions = siblingAttributeData([lineItem, ...associatedLineItems], attributeStatus);

        // Get the total price for the group.
        associatedLineItems.forEach((sibling) => {
          originalTotalLineItemAmount.centAmount += sibling.custom.fields.originalLineItemAmount.centAmount;
          discountedTotalProductPriceValue.centAmount += sibling.custom.fields.discountedProductPriceValue.centAmount;
          originalTotalLineItemAmountWithTax.centAmount
            += sibling.custom.fields.originalLineItemAmountWithTax.centAmount;
          quantity += sibling.quantity;
        });
      }

      // Get the selection for the group, as used for the getPrice endpoint.
      selection = getSelectionForPricing([lineItem, ...associatedLineItems]);
    } else if (!hasReviewGroup(lineItem)) {
      // Get the selection for the (normal) lineItem, as used for the getPrice endpoint.
      selection = getSelectionForPricing([lineItem]);
    }

    return {
      ...lineItem,
      sane: {
        ...lineItem.sane,
        lineItemQuantity: quantity,
        productDescription,
        siblingDescriptions,
        variableAttributesForPrice: selection.variableAttributesForPrice,
        groupedAttributesForPrice: selection.groupedAttributesForPrice,
        pivotAttributesForPrice: selection.pivotAttributesForPrice,
        originalTotalLineItemAmount,
        discountedTotalProductPriceValue,
        originalTotalLineItemAmountWithTax,
      },
    };
  });

  return {
    ...order,
    lineItems: sanitizedLineItemsForReOrder,
  };
};

const sanitizeOrder = (
  order: OrderWithTotalDetails,
  locale: string,
  forReOrder: boolean = false,
): SanitizedOrder => {
  const { shippingInfo } = order;
  const deliveries = shippingInfo && shippingInfo.deliveries ? shippingInfo.deliveries : [];

  // Enrich the lineItem data with the proper product name, file delivery date, timed delivery info and more....
  const sanitizedLineItems: SanitizedLineItem[] = order.lineItems
    .map((lineItem) => sanitizeLineItem(lineItem, deliveries, locale));

  if (forReOrder) {
    return sanitizeLineItemsForReOrder({
      ...order,
      lineItems: sanitizedLineItems,
    });
  }

  return {
    ...order,
    lineItems: sanitizedLineItems,
  };
};

export {
  sanitizeOrder,
  formatPostalCode,
  getFinalDeliveryDate,
};
